import { FC, useEffect, useState, useRef } from 'react'
import { FileIcon, Trash2, Upload, Loader2, Download, AlertCircle, X } from 'lucide-react'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import { ProjectFile } from '../../types/project'
import { listProjectFiles, uploadProjectFile, deleteProjectFile } from '../../services/api'

interface ProjectFilesPanelProps {
  projectId: string
}

export const ProjectFilesPanel: FC<ProjectFilesPanelProps> = ({ projectId }) => {
  const [files, setFiles] = useState<ProjectFile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadFiles()
  }, [projectId])

  const loadFiles = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const projectFiles = await listProjectFiles(projectId)
      const sortedFiles = projectFiles.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      setFiles(sortedFiles)
    } catch (err) {
      console.error('Failed to load project files:', err)
      setError('Failed to load files')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return

    const file = e.target.files[0]
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setError('File size exceeds 10MB limit')
      e.target.value = ''
      return
    }

    const uploadId = `${file.name}-${Date.now()}`
    setUploadingFiles(prev => new Set(prev).add(uploadId))
    setError(null)

    try {
      const uploadedFile = await uploadProjectFile(projectId, file)
      setFiles(prev => [uploadedFile, ...prev])
    } catch (err) {
      console.error('Failed to upload file:', err)
      setError('Failed to upload file')
    } finally {
      setUploadingFiles(prev => {
        const next = new Set(prev)
        next.delete(uploadId)
        return next
      })
      e.target.value = ''
    }
  }

  const handleDelete = async (fileId: string, filename: string) => {
    if (!confirm(`Delete ${filename}?`)) return

    try {
      await deleteProjectFile(projectId, fileId)
      setFiles(prev => prev.filter(f => f.id !== fileId))
    } catch (err) {
      console.error('Failed to delete file:', err)
      setError('Failed to delete file')
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  return (
    <div className="p-4">
      {/* Upload button */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {files.length} {files.length === 1 ? 'file' : 'files'}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingFiles.size > 0}
          className="h-8 gap-2"
        >
          {uploadingFiles.size > 0 ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-3.5 w-3.5" />
              Upload
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
          accept=".pdf,.txt,.md,.csv,.doc,.docx,.xls,.xlsx"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-6">
          <FileIcon className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No files yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map(file => (
            <div
              key={file.id}
              className={cn(
                "group flex items-center gap-3 p-3 rounded-lg",
                "bg-surface/50 hover:bg-surface transition-colors"
              )}
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <FileIcon className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.file_size)}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => window.open(file.blob_url, '_blank')}
                  className="h-7 w-7"
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(file.id, file.filename)}
                  className="h-7 w-7 hover:text-destructive"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
